import { selectRouterPathname } from '@noths/polaris-client-next-redux';
import { isBrowser } from '@noths/polaris-client-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { replace } from 'connected-next-router';
import qs from 'querystring';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPageType } from 'src/redux/navigation/selectors';
import { adaptBrowseFilters } from 'src/redux/products/adapters/browse-data-api/adaptBrowseFilters';
import { adaptBrowseProducts } from 'src/redux/products/adapters/browse-data-api/adaptBrowseProducts';
import { adaptBrowseSortOptions } from 'src/redux/products/adapters/browse-data-api/adaptBrowseSortOptions';
import {
  CATEGORY_NOT_FOUND_ERROR,
  MISSING_CATEGORY_ID_ERROR,
  PARTNER_NOT_FOUND_ERROR,
} from 'src/redux/products/thunks/thunkErrors';
import type { RequestProductsActionPayload, TriggerName } from 'src/redux/products/types';
import {
  getCategoryListingsData,
  getPartnerListingsData,
  getSearchListingsData,
} from 'src/services/browse-data-api/getListingsData';
import { PageType } from 'src/types/navigation';
import { logger } from 'src/utils/serverLogger';
import { selectProductsQueryAsUrlQuery } from '../selectors/urlSelectors';

export interface RequestProductsOptions {
  cookie?: string;
  requestIsLoadTest?: boolean;
  triggerName?: TriggerName;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions
>(
  'products/requestProducts',
  async ({ cookie, requestIsLoadTest, triggerName }, { dispatch, getState }) => {
    const state = getState() as ReduxApplicationState;
    const { pathname } = state.router.location;
    const { page } = state.productsQuery;
    const { currencyCode, deliveryZone } = state.userConfiguration;

    const queryObject = selectProductsQueryAsUrlQuery(state, page);

    if (isBrowser()) {
      const query = qs.stringify(queryObject);

      dispatch(
        replace(
          {
            pathname: selectRouterPathname(state),
            query,
          },
          query ? `${pathname}?${query}` : pathname,
          { shallow: true, scroll: false },
        ),
      );
    }

    const commonGetDataArgs = {
      cookie,
      currencyCode,
      ...(deliveryZone && { deliveryZone }),
    };
    let response;

    switch (selectPageType(state)) {
      case PageType.Search: {
        response = await getSearchListingsData({
          ...commonGetDataArgs,
          query: queryObject,
          requestIsLoadTest,
          loggedInUserId: state.user.cognitoID || undefined,
          searchTerm: state.productsQuery.canonicalFilters.searchTerm!,
          userId: state.userConfiguration.gaVisitorId,
        });
        break;
      }
      case PageType.Category: {
        const { previewing, ...restOfQuery } = queryObject;
        const categoryId = state.productsQuery.canonicalFilters.categoryIDs?.[0]
          ? state.productsQuery.canonicalFilters.categoryIDs[0]
          : null;

        if (!categoryId) {
          logger.error('category ID missing for category listing page');

          throw MISSING_CATEGORY_ID_ERROR;
        }

        response = await getCategoryListingsData({
          ...commonGetDataArgs,
          categoryId,
          ...(previewing ? { previewing: true } : {}),
          query: restOfQuery, // when categoryId is set, fetchBrowseData will not send query.path
          loggedInUserId: state.user.cognitoID || undefined,
          userId: state.userConfiguration.gaVisitorId,
        });

        if (response === null) {
          throw CATEGORY_NOT_FOUND_ERROR;
        }

        break;
      }
      case PageType.Partner: {
        response = await getPartnerListingsData({
          ...commonGetDataArgs,
          partnerShortcode: state.productsQuery.canonicalFilters.partnerShortcode!,
          query: queryObject,
          loggedInUserId: state.user.cognitoID || undefined,
          userId: state.userConfiguration.gaVisitorId,
        });
        if (response === null) {
          throw PARTNER_NOT_FOUND_ERROR;
        }
        break;
      }
      default:
        console.error('unknown page type');
    }

    if (response?.redirectUrl) {
      return { redirectUrl: response.redirectUrl };
    }

    const payload: RequestProductsActionPayload = {
      facets: adaptBrowseFilters(response.data.filters),
      products: adaptBrowseProducts(response.data),
      sortOptions: adaptBrowseSortOptions(response.data.sortingOptions),
      triggerName,
    };

    return payload;
  },
);
